class Carousel extends HTMLElement {
  connectedCallback() {
    if(this.dataset.carouselEnabled === 'false') {
      return;
    }
    this.carouselId = this.dataset.carouselId;
    if(!this.carouselId) {
      throw new Error('Carousel requires a valid ID.');
    }
    if(this.hasAttribute('data-carousel-duration')) {
      this.duration = parseFloat(this.dataset.carouselDuration);
    }
    const wait = this.dataset.carouselWait ?? false;
    if(wait) {
      setTimeout(() => {
        this.initiate();
      }, parseInt(wait))
    }
    else {
      this.initiate();
    }
  }

  initiate() {
    this.container = this.querySelector(this.dataset.carouselContainer) ?? this;
    this.items = this.container.children;
    if(this.items.length === 0) {
      throw new Error(`Carousel requires at least one item. ${this.carouselId}`)
    }
    this.animate = horizontalLoop(this.items, {paused: true});
    this.paused = false;
    const jumpTriggers = document.querySelectorAll(`[data-carousel-jump="${this.carouselId}"]`);
    jumpTriggers.forEach(trigger => {
      trigger.addEventListener('click', event => {
        event.preventDefault();
        const index = trigger.dataset.jumpTo;
        this.jumpTo(index);
        document.querySelectorAll(`[data-carousel-jump][data-state=active]`).forEach(i => {
          i.setAttribute('data-state', 'inactive');
        })
        trigger.setAttribute('data-state', 'active');
      })
    })
    this.controls = document.querySelector(`carousel-controls[data-carousel-id="${this.carouselId}"]`);
    // if(!this.controls) {
    //   throw new Error(`Carousel controls are missing. ${this.id}`)
    // }

    this.setupDragEvents();
    
    if(this.controls) {
      this.slideSpeed = parseInt(this.controls.dataset.carouselSpeed ?? 100);
      this.duration = this.duration ?? parseFloat(this.controls.dataset.carouselDuration ?? 1.5);
      this.autoSlide = this.controls.hasAttribute('data-carousel-auto')
        && this.controls.dataset.carouselAuto !== 'false';
      this.carouselPause = this.controls.hasAttribute('data-carousel-pause');
      this.looping = this.slideSpeed === 0 && this.autoSlide;
      this.setup();
      window.addEventListener(
        'resize', 
        debounce(() => {
          this.handleResize()
        },
        300).bind(this)
      );
      if(!Shopify.designMode && this.autoSlide) {
        this.observeInViewport();
      }

      // Stop the scrolling if the user changes tab. This prevents the script from playing catchup if they return to the tab.
      document.addEventListener("visibilitychange", this.stopAutoScroll.bind(this));
    }
  }

  definePauseListener() {
    this.pauseListener = debounce(() => {
      if(!this.matches(':hover')) {
        return;
      }
      this.removeEventListener('mouseover', this.pauseListener);
      this.animate.pause();
      this.pauseInterval = setInterval(() => {
        if (!this.matches(':hover')) {
          this.startAutoScroll();
          if(this.carouselPause) {
            this.definePauseListener();
          }
        }
      }, 2000)
    }, 1000)
    this.registerPauseListener();
  }

  registerPauseListener() {
    this.addEventListener('mouseover', this.pauseListener)
  }

  setup() {
    if(this.looping) {
      this.animate = horizontalLoop(this.items, {paused: true, repeat: -1, speed: this.duration === 0 ? 1 : this.duration});
      this.hideControls();
      if(this.carouselPause) {
        this.definePauseListener();
      }
      return;
    }
    const controlType = this.controls.dataset.carouselType;
    if(controlType === 'arrow') {
      this.controls.querySelector('carousel-prev-arrow')
        ?.addEventListener('click', this.prevItem.bind(this));
      this.controls.querySelector('carousel-next-arrow')
        ?.addEventListener('click', this.nextItem.bind(this));
    }
    else if(controlType === 'counters') {
      const template = this.controls.querySelector('carousel-counter').cloneNode();
      const holder = this.controls.querySelector('carousel-counter-holder');
      holder.innerHTML = '';
      for (let i = 0; i < this.items.length; i++) {
        const newNode = template.cloneNode();
        newNode.setAttribute('data-carousel-index', `${i}`);
        if(i === 0) {
          newNode.setAttribute('data-state', 'active')
        }
        holder.append(newNode)
        newNode.addEventListener('click', this.handleClickCounter.bind(this))
      }
    }
    else if(controlType === 'gallery') {
      this.controls.querySelector('carousel-prev-arrow')
        ?.addEventListener('click', this.prevGalleryItem.bind(this));
      this.controls.querySelector('carousel-next-arrow')
        ?.addEventListener('click', this.nextGalleryItem.bind(this));
    }
    this.toggleControls();
  }

  handleClickCounter(event) {
    const index = event.target.dataset.carouselIndex;
    this.jumpTo(index, 0)
  }

  activateCounter() {
    this.updateGalleryCounter();
    const active = this.controls?.querySelectorAll('carousel-counter[data-state=active]') ?? [];
    active.forEach(i => i.setAttribute('data-state', 'inactive'));
    this.controls?.querySelector(`carousel-counter[data-carousel-index="${this.animate.current()}"]`)
      ?.setAttribute('data-state', 'active');
  }

  handleResize() {
    this.toggleControls();
  }

  isVisible(element) {
    const rect = element.getBoundingClientRect();
    const viewport = this.getBoundingClientRect();
    
    return rect.right >= viewport.left && rect.right <= viewport.right; 
  }

  toggleControls() {
    if(!this.controls) {
      return false;
    }
    let visible = false;
    for (const i of this.items) {
      if(!this.isVisible(i)) {
        visible = true;
        break;
      }
    }
    visible ? this.showControls() : this.hideControls();
  }

  showControls() {
    this.controls.style.display = 'flex';
    setTimeout(() => {
      this.controls.style.opacity = 1;
    }, 100)
  }

  hideControls() {
    this.controls.style.opacity = 0;
    setTimeout(() => {
      this.controls.style.display = 'none';
    }, 150)
  }

  observeInViewport() {
    this.observer && this.observer.disconnect();
    this.observer = new IntersectionObserver(function(entries) {
      if(entries[0].isIntersecting === true) {
        entries[0]['target'].startAutoScroll();
      }
    }, { threshold: [0]});
    if(!Shopify.designMode) {
      this.observer.observe(this);
    }
  }

  observeLeavesViewport() {
    this.observer && this.observer.disconnect();
    this.observer = new IntersectionObserver(function(entries) {
      if(entries[0].isIntersecting !== true) {
        entries[0]['target'].stopAutoScroll();
      }
    }, { threshold: [0]});
    this.observer.observe(this);
  }

  startAutoScroll() {
    if(this.looping) {
      this.animate.play();
    }
    else {
      this.setAutoInterval();
    }
    this.observeLeavesViewport();
  }

  stopAutoScroll() {
    this.interval && clearInterval(this.interval);
    if(this.looping) {
      this.animate.pause();
    }
    this.observeInViewport();
  }

  resetAutoInterval() {
    this.interval && clearInterval(this.interval);
    this.setAutoInterval();
  }

  setAutoInterval() {
    if(this.autoSlide) {
      if(!Shopify.designMode) {
        this.interval = setInterval(() => {
          if(this.carouselPause) {
            if (this.matches(':hover')) return;
          }
          this.nextItem();
        }, this.slideSpeed * 1000)
      }
    }
  }

  dispatchChangeEvent() {
    if(this.slideSpeed > 0 || !this.autoSlide) {
      this.dispatchEvent(
        new CustomEvent('slide', {
          bubbles: true,
          detail: {
            currentSlide: this.animate.current()
          }
        })
      )
    }
  }

  nextItem() {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.next({duration: this.duration ?? 1.5, ease: "power1.inOut"});
    this.activateCounter();
    this.dispatchChangeEvent();
  }

  prevItem() {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.previous({duration: this.duration ?? 1.5, ease: "power1.inOut"});
    this.activateCounter();
    this.dispatchChangeEvent();
  }

  nextGalleryItem() {
    this.nextItem();
  }

  prevGalleryItem() {
    this.prevItem();
  }

  updateGalleryCounter() {
    if(this.controls.dataset.carouselType === 'gallery') {
      const index = this.controls?.querySelector('carousel-index');
      index.textContent = `${this.animate.current() + 1}`
    }
  }

  jumpTo(index, customDuration = null) {
    pauseAllMedia(this);
    this.resetAutoInterval();
    this.animate.toIndex(parseInt(index), {duration: customDuration ?? this.duration ?? 1.5, ease: "power1.inOut"})
    this.activateCounter();
    this.dispatchChangeEvent();
  }

  setupDragEvents() {
    this.addEventListener('touchstart', this.setTouchPageX.bind(this), { passive: false })
    this.addEventListener('touchend', this.checkTouchDragged.bind(this), { passive: false })
  }

  setTouchPageX(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    this.screenX = touch.screenX;
  }

  checkTouchDragged(e) {
    const evt = (typeof e.originalEvent === 'undefined') ? e : e.originalEvent;
    const touch = evt.touches[0] || evt.changedTouches[0];
    if(touch.screenX < this.screenX) {
      if((this.screenX - touch.screenX) > 40) {
        this.nextItem();
      }
    }
    else if(touch.screenX > this.screenX) {
      if((touch.screenX - this.screenX) > 40) {
        this.prevItem();
      }
    }
  }
}

customElements.define('carousel-block', Carousel);

class ScrollingText extends HTMLElement {
  connectedCallback() {
    this.scrollSpeed = parseFloat(this.dataset.scrollSpeed) ?? 1.5;
    this.reversed = this.dataset.scrollReverse === 'true';
    this.items = this.children;
    if(this.items.length === 0) {
      throw new Error(`Scrolling text requires at least one item. ${this.carouselId}`)
    }
    this.fillScroll();
    setTimeout(() => {
      this.setupScroll();
    }, 2000) 
  }

  fillScroll() {
    this.sectionWidth = this.getBoundingClientRect().width;
    const size = this.items.length;
    while(this.sectionWidth < (window.innerWidth * 1.5)) {
      for (let i = 0; i < size; i++) {
        const newItem = this.items[i].cloneNode();
        newItem.innerHTML = this.items[i].innerHTML;
        newItem.removeAttribute('id');
        this.append(newItem)
      }
      this.sectionWidth = this.getBoundingClientRect().width;
    }
  }

  setupScroll() {
    this.loop = horizontalLoop(this.items, {paused: true, repeat: -1, speed: this.speed});
    this.observeInViewport();
    // Stop the scrolling if the user changes tab. This prevents the script from playing catchup if they return to the tab.
    document.addEventListener("visibilitychange", this.stopAutoScroll.bind(this));
  }

  stopAutoScroll() {
    this.interval && clearInterval(this.interval);
    this.loop.pause();
    this.observeInViewport();
  }

  startAutoScroll() {
    this.observeLeavesViewport();
    if(this.reversed) {
      this.loop.vars.onReverseComplete();
      this.loop.reverse();
    }
    else {
      this.loop.play();
    }
  }

observeInViewport() {
    this.observer && this.observer.disconnect();
    this.observer = new IntersectionObserver(function(entries) {
      if(entries[0].isIntersecting === true) {
        entries[0]['target'].startAutoScroll();
      }
    }, { threshold: [0]});
    this.observer.observe(this);
  }

  observeLeavesViewport() {
    this.observer && this.observer.disconnect();
    this.observer = new IntersectionObserver(function(entries) {
      if(entries[0].isIntersecting !== true) {
        entries[0]['target'].stopAutoScroll();
      }
    }, { threshold: [0]});
    this.observer.observe(this);
  }
}

customElements.define('scrolling-text', ScrollingText)